import * as React from "react";

import {Table} from "semantic-ui-react";

import {ISpecConfiguration} from "./SpecConfiguration";
import {SpecConfigurationDto, SpecDto, SpecDtoSpecCategory} from "../../../services/Client";
import {DerivateUtils} from "../../../components/Table/components/derivate/DerivateUtils";

export interface IGroupMap {
    [groupName: string]: (SpecDto | undefined)[];
}

export interface ISetSpecs {
    [groupName: string]: SpecDto[];
}

interface WeightedSpecConfigurations {
    weight: number;
    sc: SpecConfigurationDto;
}

/**
 * Creates an initial map with the available categories as key and empty list as values
 *
 * @param groupMap
 *      contains the available spec dtos mapped by the category
 */
const initISetSpec = (groupMap?: IGroupMap): ISetSpecs => {
    let setSpecs: ISetSpecs = {};
    if (groupMap) {
        Object.keys(groupMap).map((key) => setSpecs[key] = []);
    }

    return setSpecs;
};

/**
 * Creates a dto based on the view selected fields. The applied specs are not set here.
 *
 * @param specConfig
 *      a dto that contains data related to the fields selected in the view
 */
export const toSpecConfigurationDto = (specConfig: ISpecConfiguration): SpecConfigurationDto => {
    return {
        objectId: 0,
        versionId: 0,
        modelyearId: specConfig.modelyear ? specConfig.modelyear.objectId : 0,
        cnrId: specConfig.cnr ? specConfig.cnr.objectId : undefined,
        importerId: specConfig.importer ? specConfig.importer.objectId : undefined,
        typefamilyId: specConfig.typeFamily ? specConfig.typeFamily.objectId : undefined,
        modeltypebasicId: specConfig.modeltypeBasic ? specConfig.modeltypeBasic.objectId : undefined,
        specs: []
    };
};

/**
 * Set the (tree)level for a specification configuration dto
 *
 * @param sc
 *      a specification configuration dto
 */
const specConfigWeight = (sc: SpecConfigurationDto): number => {
    let toRet = 0;
    if (sc.cnrId && sc.cnrId > 0)
        toRet++;
    if (sc.importerId && sc.importerId > 0)
        toRet++;
    if (sc.typefamilyId && sc.typefamilyId > 0)
        toRet++;
    if (sc.modeltypebasicId && sc.modeltypebasicId > 0)
        toRet++;
    return toRet;
};

const undefineId = (id: number | undefined): number | undefined => {
    if (id && id > 0)
        return id;
    return undefined;
};

/**
 * For the given dto, the specs are separated based on category
 *
 * @param sc
 *      a specification configuration dto
 * @param specsDS
 *      a list with the available spec dtos
 */
const specForSpecConfig = (sc: SpecConfigurationDto, specsDS: SpecDto[] | undefined): ISetSpecs => {
    let toReturn: ISetSpecs = {};

    if (specsDS &&
        sc.specs.length > 0) {
        sc.specs.forEach((val) => {
            let idx = specsDS.findIndex((v) => v.specCode === val);
            if (idx !== -1) {
                if (!toReturn[specsDS[idx].specCategory]) {
                    toReturn[specsDS[idx].specCategory] = [specsDS[idx]];
                } else {
                    toReturn[specsDS[idx].specCategory] = Array.prototype.concat(toReturn[specsDS[idx].specCategory], specsDS[idx]);
                }
            }
        });
    }

    return toReturn;
};

/**
 * Builds the element for displaying the applied specification result
 *
 * @param selSpecs
 *      a dto that contains the selected fields
 * @param specs
 *      an object that containes the applied configuration for the selected fields
 * @param header
 *      header message
 */
export const showSpecs = (selSpecs: ISpecConfiguration, specs: ISetSpecs, header: string, userLanguage: string): JSX.Element => {
    return (
        <Table className="SpecConfigurationResultTable">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell colSpan={2}>
                        {header}
                    </Table.HeaderCell>
                </Table.Row>
                <Table.Row>
                    <Table.HeaderCell colSpan={2}>
                        {
                            (selSpecs.modelyear ? selSpecs.modelyear.mj1 + " (" + selSpecs.modelyear.mj4 + ")" : "") +
                            (selSpecs.cnr ? ", " + selSpecs.cnr.cnr : "") +
                            (selSpecs.importer ? ", " + selSpecs.importer.importer : "") +
                            (selSpecs.typeFamily ? ", " + selSpecs.typeFamily.name : "") +
                            (selSpecs.modeltypeBasic ?
                                (DerivateUtils.renderDerivateName(selSpecs.modeltypeBasic, userLanguage)
                                    ? ", " + DerivateUtils.renderDerivateName(selSpecs.modeltypeBasic, userLanguage)
                                    : "")
                                : "")
                        }
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {Object.keys(specs).map((key, idx) => (
                    <Table.Row key={idx}>
                        <Table.Cell>
                            {key}
                        </Table.Cell>
                        <Table.Cell>
                            {specs[key].map((val) => val.specCode).join(', ')}
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table.Body>
        </Table>
    );
};

/**
 * Builds a map based on the specs categories
 *
 * @param specsDS
 *      a list with the available spec dtos
 */
export const createGroupMap = (specsDS: SpecDto[] | undefined): IGroupMap | undefined => {
    if (specsDS) {
        let gm: IGroupMap = {};
        specsDS.forEach((val, idx) => {
            if (!gm[val.specCategory])
                gm[val.specCategory] = [];
            gm[val.specCategory].push(val);
        });
        return gm;
    }

    return undefined;
};

/**
 * Checks if the parent is below or equal with the selection level(child)
 *
 * @param parent
 *      a specification configuration dto
 * @param child
 *      a dummy dto with fields that represent the selection level
 */
const scIsBelowOrEqual = (parent: SpecConfigurationDto, child: SpecConfigurationDto): boolean => {
    let pCnr = undefineId(parent.cnrId);
    let pImp = undefineId(parent.importerId);
    let pTf = undefineId(parent.typefamilyId);
    let pMtb = undefineId(parent.modeltypebasicId);

    if (parent.modelyearId === child.modelyearId &&
        (!pCnr || (pCnr === undefineId(child.cnrId))) &&
        (!pImp || (pImp === undefineId(child.importerId))) &&
        (!pTf || (pTf === undefineId(child.typefamilyId))) &&
        (!pMtb || (pMtb === undefineId(child.modeltypebasicId))))
        return true;

    return false;
};

/**
 * Checks if the parent is equal with the selection level(child)
 *
 * @param parent
 *      a specification configuration dto
 * @param child
 *      a dummy dto with fields that represent the selection level
 */
export const scIsEqual = (parent: SpecConfigurationDto, child: SpecConfigurationDto): boolean => {
    let pCnr = undefineId(parent.cnrId);
    let pImp = undefineId(parent.importerId);
    let pTf = undefineId(parent.typefamilyId);
    let pMtb = undefineId(parent.modeltypebasicId);

    if (parent.modelyearId === child.modelyearId &&
        (pCnr === undefineId(child.cnrId)) &&
        (pImp === undefineId(child.importerId)) &&
        (pTf === undefineId(child.typefamilyId)) &&
        (pMtb === undefineId(child.modeltypebasicId)))
        return true;

    return false;
};

/**
 * Calculate the applied specification. The search is done from the higher level and stops when values for both categories were found.
 *
 * @param scLevels
 *      contains the specification configurations that are below or equal with the selection level and their level
 * @param specsDS
 *      a list with the available spec dtos
 * @param setSpecs
 *      a map where the default values have been set for the available spec categories
 */
const getAppliedSpecification = (scLevels: WeightedSpecConfigurations[], specsDS: SpecDto[] | undefined, setSpecs: ISetSpecs): ISetSpecs => {
    let foundRestSpecs = false;
    let foundAbgasSpec = false;

    // sort desc based on level
    let sortedSCLevels = scLevels.sort((a, b) => b.weight - a.weight);
    for (const val of sortedSCLevels) {
        let specs = specForSpecConfig(val.sc, specsDS);
        if (!foundRestSpecs || !foundAbgasSpec) {
            for (const key of Object.keys(specs)) {
                switch (key) {
                    case SpecDtoSpecCategory.ABGAS:
                        if (!foundAbgasSpec) {
                            setSpecs[key] = specs[key];
                            foundAbgasSpec = true;
                        }
                        break;
                    case SpecDtoSpecCategory.REST:
                        if (!foundRestSpecs) {
                            setSpecs[key] = specs[key];
                            foundRestSpecs = true;
                        }
                        break;
                    default:
                        break;
                }
            }
        } else {
            break;
        }
    }

    return setSpecs;
};

/**
 * Process the specification configurations that are below or equal with the selection level and return the applied specification.
 *
 * @param specConfig
 *      a dto that contains data for the selection level
 * @param specsDS
 *      a list with the available spec dtos
 * @param specConfigList
 *      a list with all specification configuration dtos
 * @param groupMap
 *      contains the available spec dtos mapped by the category
 */
export const getSpecsForSelected = (levelSpecConfig: ISpecConfiguration, specsDS: SpecDto[] | undefined, specConfigList: SpecConfigurationDto[], groupMap?: IGroupMap):
    ISetSpecs => {

    if (!levelSpecConfig.modelyear)
        return {};

    let setSpecs: ISetSpecs = initISetSpec(groupMap);
    if (specConfigList.length > 0) {
        // selection level dto
        let selConfigLevel = toSpecConfigurationDto(levelSpecConfig);

        // add only specs below or equal to the selection level
        let res: WeightedSpecConfigurations[] = [];
        specConfigList.forEach((val) => {
            if (scIsBelowOrEqual(val, selConfigLevel)) {
                res.push({
                    weight: specConfigWeight(val),
                    sc: val
                });
            }
        });

        return getAppliedSpecification(res, specsDS, setSpecs);
    }

    return setSpecs;
};



// WEBPACK FOOTER //
// ./src/views/AdminView/SpecConfigTab/SpecConfigurationHelpers.tsx